import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';
import { logger } from "@/utils/logger";

export interface ConsentCategories {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    functional: boolean;
}

export interface CookieConsentData {
    id?: string;
    user_id?: string;
    session_id?: string;
    consent_categories: ConsentCategories;
    consent_version: string;
    updated_at?: string;
}

const STORAGE_KEY = 'cookie_consent_preferences';
const SESSION_ID_KEY = 'cookie_consent_session_id';
const CURRENT_POLICY_VERSION = '1.0';

export const CookieConsentService = {
    // Get current consent from local storage
    getConsent: (): ConsentCategories | null => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) return null;

            const parsed = JSON.parse(stored);
            // Check if version matches, if not, might need re-consent
            // For now, we just return what's there, but UI might prompt if version changed
            return parsed.categories;
        } catch (e) {
            logger.error("Error parsing cookie consent:", e);
            return null;
        }
    },

    // Save consent to local storage and database
    saveConsent: async (categories: ConsentCategories): Promise<void> => {
        try {
            // 1. Save to Local Storage
            const consentData = {
                categories,
                version: CURRENT_POLICY_VERSION,
                timestamp: new Date().toISOString()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));

            // 2. Get or Create Session ID
            let sessionId = localStorage.getItem(SESSION_ID_KEY);
            if (!sessionId) {
                sessionId = uuidv4();
                localStorage.setItem(SESSION_ID_KEY, sessionId);
            }

            // 3. Save to Database
            let { data: { user } } = await supabase.auth.getUser();

            // PWA Specific: If user is null, try to re-establish session from local storage backup if available
            if (!user) {
                const session = localStorage.getItem('supabase.auth.token');
                if (session) {
                    // Force a session refresh check
                    const { data: { session: refreshedSession } } = await supabase.auth.getSession();
                    if (refreshedSession?.user) {
                        user = refreshedSession.user;
                    }
                }
            }

            const dbPayload = {
                user_id: user?.id || null,
                session_id: sessionId,
                consent_categories: categories,
                consent_version: CURRENT_POLICY_VERSION,
                user_agent: navigator.userAgent,
                // ip_address is typically handled by the server/database or edge function, 
                // but we can't easily get it client-side without an external service.
                // Supabase might capture it in request logs, but for the table we might leave it null 
                // or have a backend function fill it.
            };

            // We want to upsert based on user_id OR session_id.
            // Since we can't easily do a complex OR upsert in one go without a unique constraint that covers both nullable fields,
            // we'll try to find an existing record first.

            let existingRecord = null;

            if (user?.id) {
                const { data } = await supabase
                    .from('user_cookie_consent' as any)
                    .select('id')
                    .eq('user_id', user.id)
                    .maybeSingle();
                existingRecord = data;
            } else {
                const { data } = await supabase
                    .from('user_cookie_consent' as any)
                    .select('id')
                    .eq('session_id', sessionId)
                    .is('user_id', null) // Only match anonymous records
                    .maybeSingle();
                existingRecord = data;
            }

            if (existingRecord) {
                await supabase
                    .from('user_cookie_consent' as any)
                    .update({
                        consent_categories: categories,
                        consent_version: CURRENT_POLICY_VERSION,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', existingRecord.id);
            } else {
                await supabase
                    .from('user_cookie_consent' as any)
                    .insert(dbPayload);
            }

            // 4. Trigger event for immediate effect
            window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: categories }));

        } catch (error) {
            logger.error("Failed to save cookie consent:", error);
            // Fallback: Local storage is already updated, so the app will behave correctly locally.
        }
    },

    // Check if a specific category is allowed
    isAllowed: (category: keyof ConsentCategories): boolean => {
        if (category === 'essential') return true;
        const consent = CookieConsentService.getConsent();
        return consent ? consent[category] : false;
    },

    // Clear consent (e.g. for testing or "Reset" button)
    clearConsent: () => {
        localStorage.removeItem(STORAGE_KEY);
        // We don't necessarily delete from DB, just clear local preference to trigger banner again
    },

    // Delete consent from database (used during logout)
    deleteConsent: async (): Promise<void> => {
        try {
            logger.info("Attempting to delete consent...");

            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
                logger.info("Deleting cookie consent for user:", user.id);
                const { error, count } = await supabase
                    .from('user_cookie_consent' as any)
                    .delete({ count: 'exact' })
                    .eq('user_id', user.id);

                if (error) {
                    logger.error("Supabase delete error:", error);
                } else {
                    logger.info(`Deleted ${count} consent records for user ${user.id}`);
                }
            } else {
                logger.warn("No user found during deleteConsent call");
            }

            // Also clear local storage
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(SESSION_ID_KEY);
            logger.info("Local storage consent cleared");

        } catch (error) {
            logger.error("Error deleting cookie consent:", error);
        }
    },

    // Sync consent from database (for logged-in users)
    syncWithDatabase: async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // First, check if there's an anonymous record with our session_id that needs migration
            const sessionId = localStorage.getItem(SESSION_ID_KEY);
            if (sessionId) {
                // Use RPC to securely migrate anonymous record to this user (bypassing RLS)
                logger.info("Attempting to migrate anonymous consent record...");
                const { error: migrationError } = await supabase.rpc('migrate_consent_to_user' as any, {
                    current_session_id: sessionId
                });

                if (migrationError) {
                    logger.error("Error migrating consent:", migrationError);
                } else {
                    logger.info("Migration RPC executed successfully");
                }
            }

            // Now fetch the user's consent record
            const { data, error } = await supabase
                .from('user_cookie_consent' as any)
                .select('consent_categories')
                .eq('user_id', user.id)
                .order('updated_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                logger.error("Error syncing cookie consent from DB:", error);
                return;
            }

            if (data) {
                // Cast data to any to access properties since we used 'as any' for the table
                const record = data as any;
                if (record.consent_categories) {
                    logger.info("Syncing cookie consent from DB:", record.consent_categories);
                    // Update LocalStorage to match DB
                    const consentData = {
                        categories: record.consent_categories,
                        version: CURRENT_POLICY_VERSION,
                        timestamp: new Date().toISOString()
                    };
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData));

                    // Trigger event for immediate effect
                    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: record.consent_categories }));
                }
            }
        } catch (e) {
            logger.error("Failed to sync cookie consent:", e);
        }
    }
};
